import React, { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "@/hooks/useApi";
import MoshkyAnalyticsPanel from "@/components/MoshkyAnalyticsPanel";
import useSubscriptions from "@/hooks/useSubscriptions";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";

// ── Types ──────────────────────────────────────────────────────────────────

interface ThemeCount {
  category: string;
  total: number;
  resolvedCount: number;
  missedCount: number;
  aiResolvedCount: number;
  pct: number;
}

interface PeriodData {
  period: string;
  totalConversations: number;
  resolvedCount: number;
  missedCount: number;
  aiResolvedCount: number;
  themes: ThemeCount[];
}

interface BenchmarkComparison {
  themeCategory: string;
  sitePct: number;
  industryMedianPct: number;
  industryP25Pct: number;
  industryP75Pct: number;
  industryVertical: string;
  siteCount: number;
  delta: number;
}

interface KeyValueCount {
  key: string;
  count: number;
}

interface ChatIntelligenceReport {
  siteId: number;
  start: string;
  end: string;
  totalConversations: number;
  resolvedCount: number;
  missedCount: number;
  aiResolvedCount: number;
  deflectedCount: number;
  themes: ThemeCount[];
  missedByReason: KeyValueCount[];
  missedByTheme: KeyValueCount[];
  missedByPage: KeyValueCount[];
  periods: PeriodData[];
  benchmarkComparisons: BenchmarkComparison[];
}

// ── Helpers ────────────────────────────────────────────────────────────────

const THEME_COLORS = [
  "#6366f1", "#0ea5e9", "#22c55e", "#f59e0b", "#ef4444",
  "#8b5cf6", "#14b8a6", "#f97316", "#ec4899", "#64748b",
];

function pct(n: number, total: number) {
  if (total === 0) return "0%";
  return `${Math.round((n / total) * 100)}%`;
}

function SummaryCard({
  label, value, sub, color,
}: { label: string; value: string; sub: string; color: string }) {
  return (
    <div
      style={{
        background: "var(--v-bg-card)",
        border: "1px solid var(--v-border)",
        borderRadius: 10,
        padding: "16px 20px",
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--v-text-muted)", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--v-text-muted)", marginTop: 4 }}>{sub}</div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────

export default function ChatIntelligence() {
  const api = useApi();
  const { subscription: subs } = useSubscriptions();
  const [data, setData] = useState<ChatIntelligenceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(90);

  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    return {
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
    };
  }, [days]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.messaging.get(
        `Reports/chat-intelligence/themes?startDate=${startDate}&endDate=${endDate}&groupBy=month`
      );
      if (res.status === 403) {
        setError("Chat Intelligence is not enabled for this account.");
        return;
      }
      if (!res.ok) throw new Error("Failed to load");
      const json = await res.json();
      setData(json);
    } catch {
      setError("Could not load Chat Intelligence data.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  if (!subs?.enableChatIntelligenceReport) {
    return (
      <div style={{ textAlign: "center", padding: "60px 24px", color: "var(--v-text-muted)" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🧠</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Chat Intelligence not enabled</div>
        <p style={{ fontSize: 13 }}>
          Contact your Velaro account team to enable Chat Intelligence for this account.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 m-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        {error}{" "}
        {error !== "Chat Intelligence is not enabled for this account." && (
          <button className="ml-2 text-xs font-medium text-slate-600 border border-slate-300 rounded px-2 py-1 hover:bg-slate-50 transition-colors" onClick={load}>Retry</button>
        )}
      </div>
    );
  }

  if (!data) return null;

  const { totalConversations, resolvedCount, missedCount, aiResolvedCount, deflectedCount, themes, periods, benchmarkComparisons, missedByTheme, missedByPage } = data;
  const topThemes = [...themes].sort((a, b) => b.total - a.total).slice(0, 10);

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Chat Intelligence</h2>
          <p style={{ margin: "4px 0 0", color: "var(--v-text-muted)", fontSize: 13 }}>
            Conversation theme analysis · {startDate} – {endDate}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <MoshkyAnalyticsPanel reportName="Chat Intelligence" reportData={{ totalConversations, resolvedCount, missedCount, aiResolvedCount, deflectedCount, topThemes, periods }} />
          {[30, 60, 90, 180].map((d) => (
            <button
              key={d}
              className={`text-xs px-2.5 py-1 rounded border font-medium transition-colors ${days === d ? "bg-primary text-white border-primary" : "text-slate-600 border-slate-300 hover:bg-slate-50"}`}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16, marginBottom: 28 }}>
        <SummaryCard label="Total Conversations" value={totalConversations.toLocaleString()} sub="in selected period" color="#6366f1" />
        <SummaryCard label="Resolved by Agent" value={resolvedCount.toLocaleString()} sub={pct(resolvedCount, totalConversations) + " of total"} color="#22c55e" />
        <SummaryCard label="AI Resolved" value={aiResolvedCount.toLocaleString()} sub={pct(aiResolvedCount, totalConversations) + " of total"} color="#0ea5e9" />
        <SummaryCard label="Bot Deflected" value={deflectedCount.toLocaleString()} sub={pct(deflectedCount, totalConversations) + " of total"} color="#8b5cf6" />
        <SummaryCard label="Missed" value={missedCount.toLocaleString()} sub={pct(missedCount, totalConversations) + " of total"} color="#ef4444" />
      </div>

      {/* Theme Distribution */}
      {topThemes.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Theme Distribution</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

            {/* Bar chart */}
            <div style={{ background: "var(--v-bg-card)", border: "1px solid var(--v-border)", borderRadius: 10, padding: "16px 20px" }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topThemes} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--v-border)" />
                  <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="category" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: unknown) => [`${(v as number).toFixed(1)}%`, "Share"]} />
                  <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
                    {topThemes.map((_, i) => (
                      <Cell key={i} fill={THEME_COLORS[i % THEME_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Table */}
            <div style={{ background: "var(--v-bg-card)", border: "1px solid var(--v-border)", borderRadius: 10, overflow: "hidden" }}>
              <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "var(--v-bg-subtle)" }}>
                    <th>Theme</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                    <th style={{ textAlign: "right" }}>Missed</th>
                    <th style={{ textAlign: "right" }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {topThemes.map((t, i) => (
                    <tr key={t.category}>
                      <td>
                        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: THEME_COLORS[i % THEME_COLORS.length], marginRight: 6 }} />
                        {t.category}
                      </td>
                      <td style={{ textAlign: "right" }}>{t.total.toLocaleString()}</td>
                      <td style={{ textAlign: "right", color: t.missedCount > 0 ? "#ef4444" : "inherit" }}>{t.missedCount}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{t.pct.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Period Trend */}
      {periods.length > 1 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Volume Trend</h3>
          <div style={{ background: "var(--v-bg-card)", border: "1px solid var(--v-border)", borderRadius: 10, padding: "16px 20px" }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={periods} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--v-border)" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="totalConversations" name="Total" stroke="#6366f1" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="resolvedCount" name="Resolved" stroke="#22c55e" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="missedCount" name="Missed" stroke="#ef4444" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="aiResolvedCount" name="AI Resolved" stroke="#0ea5e9" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Missed by Theme */}
      {missedByTheme.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 32 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Missed by Theme</h3>
            <div style={{ background: "var(--v-bg-card)", border: "1px solid var(--v-border)", borderRadius: 10, overflow: "hidden" }}>
              <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "var(--v-bg-subtle)" }}>
                    <th>Theme</th>
                    <th style={{ textAlign: "right" }}>Missed</th>
                  </tr>
                </thead>
                <tbody>
                  {missedByTheme.slice(0, 8).map((t) => (
                    <tr key={t.key}>
                      <td>{t.key}</td>
                      <td style={{ textAlign: "right", color: "#ef4444", fontWeight: 600 }}>{t.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Missed by Page */}
          {missedByPage.length > 0 && (
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Missed by Page</h3>
              <div style={{ background: "var(--v-bg-card)", border: "1px solid var(--v-border)", borderRadius: 10, overflow: "hidden" }}>
                <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "var(--v-bg-subtle)" }}>
                      <th>Page</th>
                      <th style={{ textAlign: "right" }}>Missed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missedByPage.slice(0, 8).map((p) => (
                      <tr key={p.key}>
                        <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.key}>{p.key}</td>
                        <td style={{ textAlign: "right", color: "#ef4444", fontWeight: 600 }}>{p.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Benchmark Comparisons */}
      {benchmarkComparisons.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Industry Benchmarks</h3>
          <div style={{ background: "var(--v-bg-card)", border: "1px solid var(--v-border)", borderRadius: 10, overflow: "hidden" }}>
            <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--v-bg-subtle)" }}>
                  <th>Theme</th>
                  <th style={{ textAlign: "right" }}>Your %</th>
                  <th style={{ textAlign: "right" }}>Industry Median</th>
                  <th style={{ textAlign: "right" }}>Delta</th>
                  <th>Vertical</th>
                </tr>
              </thead>
              <tbody>
                {benchmarkComparisons.map((b) => (
                  <tr key={b.themeCategory}>
                    <td>{b.themeCategory}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{b.sitePct.toFixed(1)}%</td>
                    <td style={{ textAlign: "right" }}>{b.industryMedianPct.toFixed(1)}%</td>
                    <td style={{ textAlign: "right", color: b.delta > 5 ? "#ef4444" : b.delta < -5 ? "#22c55e" : "var(--v-text-secondary)", fontWeight: 600 }}>
                      {b.delta > 0 ? "+" : ""}{b.delta.toFixed(1)}pp
                    </td>
                    <td style={{ color: "var(--v-text-muted)" }}>{b.industryVertical}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalConversations === 0 && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "var(--v-text-muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🧠</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No data yet for this period</div>
          <p style={{ fontSize: 13 }}>
            Chat Intelligence classifies conversations automatically after they resolve.
            Expand the date range or wait for more conversations to accumulate.
          </p>
        </div>
      )}
    </div>
  );
}
